import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Icon, Input, Modal } from '@mysaiva/core'
import type { ApiKey, ApiKeySecret } from '../../api-keys/apiKeys.types'
import { SecretBox } from './SecretBox'
import styles from './ApiKeys.module.scss'

interface BaseModalProps {
  apiKey: ApiKey
  open: boolean
  onCancel: () => void
  loading?: boolean
}

interface RotateKeyModalProps extends BaseModalProps {
  onConfirm: () => void
  /** Once set, the modal switches to the new-secret reveal step. */
  result: ApiKeySecret | null
  onSecretCopied?: () => void
  onDone: () => void
}

/**
 * Two-step rotation: confirmation with the overlap-window copy, then the new
 * secret. The secret is shown only once, so the reveal step cannot be
 * dismissed (no close button, Done, mask or Escape) until it was copied
 * (design annotation, review SAIV-11493). The confirmation step locks the
 * same way while the request is pending: a rotation that succeeds after the
 * dialog was cancelled would reveal its one-time secret into a closed modal.
 */
export function RotateKeyModal({
  apiKey,
  open,
  onCancel,
  onConfirm,
  loading,
  result,
  onSecretCopied,
  onDone
}: RotateKeyModalProps) {
  const { t } = useTranslation()
  const revealed = result !== null
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!open) setCopied(false)
  }, [open])

  const handleCopied = () => {
    setCopied(true)
    onSecretCopied?.()
  }

  const dismissible = revealed ? copied : !loading

  let footer: ReactNode = null
  if (!revealed) {
    footer = (
      <div className={styles.modalFooter}>
        <Button type="text" disabled={loading} onClick={onCancel} data-testid="rotate-cancel">
          {t('developerPlatform.apiKeys.actions.cancel')}
        </Button>
        <Button type="primary" loading={loading} onClick={onConfirm} data-testid="rotate-confirm">
          {t('developerPlatform.apiKeys.rotate.confirm')}
        </Button>
      </div>
    )
  } else if (copied) {
    footer = (
      <div className={styles.modalFooter}>
        <Button type="primary" onClick={onDone} data-testid="rotate-done">
          {t('developerPlatform.apiKeys.actions.done')}
        </Button>
      </div>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={revealed ? onDone : onCancel}
      closable={dismissible}
      keyboard={dismissible}
      maskClosable={!revealed && !loading}
      title={
        <span className={styles.modalTitle}>
          {revealed
            ? t('developerPlatform.apiKeys.rotate.revealTitle')
            : t('developerPlatform.apiKeys.rotate.title', { name: apiKey.name })}
        </span>
      }
      footer={footer}
      data-testid="rotate-modal"
    >
      <div className={styles.modalBody} data-copied={copied || undefined}>
        {revealed ? (
          <>
            <p>{t('developerPlatform.apiKeys.rotate.revealText')}</p>
            <SecretBox
              secret={result.secret}
              copyLabel={t('developerPlatform.apiKeys.actions.copySecret')}
              copiedLabel={t('developerPlatform.apiKeys.actions.copied')}
              onCopied={handleCopied}
              data-testid="rotate-secret"
            />
            <div className={styles.warning} role="alert">
              <Icon icon="WarningCircle" size={20} />
              <span>{t('developerPlatform.apiKeys.created.warning')}</span>
            </div>
          </>
        ) : (
          <p>{t('developerPlatform.apiKeys.rotate.text')}</p>
        )}
      </div>
    </Modal>
  )
}

interface DisableKeyModalProps extends BaseModalProps {
  onConfirm: () => void
}

export function DisableKeyModal({
  apiKey,
  open,
  onCancel,
  onConfirm,
  loading
}: DisableKeyModalProps) {
  const { t } = useTranslation()
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={
        <span className={styles.modalTitle}>
          {t('developerPlatform.apiKeys.disable.title', { name: apiKey.name })}
        </span>
      }
      footer={
        <div className={styles.modalFooter}>
          <Button type="text" onClick={onCancel} data-testid="disable-cancel">
            {t('developerPlatform.apiKeys.actions.cancel')}
          </Button>
          <Button
            type="primary"
            loading={loading}
            onClick={onConfirm}
            data-testid="disable-confirm"
          >
            {t('developerPlatform.apiKeys.actions.disable')}
          </Button>
        </div>
      }
      data-testid="disable-modal"
    >
      <div className={styles.modalBody}>
        <p>{t('developerPlatform.apiKeys.disable.text')}</p>
      </div>
    </Modal>
  )
}

interface DeleteKeyModalProps extends BaseModalProps {
  onConfirm: () => void
}

/** Deletion is gated by retyping the key name. */
export function DeleteKeyModal({
  apiKey,
  open,
  onCancel,
  onConfirm,
  loading
}: DeleteKeyModalProps) {
  const { t } = useTranslation()
  const [confirmation, setConfirmation] = useState('')
  const matches = confirmation.trim() === apiKey.name

  const cancel = () => {
    setConfirmation('')
    onCancel()
  }

  return (
    <Modal
      open={open}
      onCancel={cancel}
      title={
        <span className={styles.modalTitle}>
          <span className={styles.modalDangerIcon} aria-hidden="true">
            <Icon icon="WarningCircle" size={18} />
          </span>
          {t('developerPlatform.apiKeys.delete.title', { name: apiKey.name })}
        </span>
      }
      footer={
        <div className={styles.modalFooter}>
          <Button type="text" onClick={cancel} data-testid="delete-cancel">
            {t('developerPlatform.apiKeys.actions.cancel')}
          </Button>
          <Button
            type="primary"
            danger
            className={styles.dangerButton}
            disabled={!matches}
            loading={loading}
            onClick={onConfirm}
            data-testid="delete-confirm"
          >
            {t('developerPlatform.apiKeys.delete.confirm')}
          </Button>
        </div>
      }
      data-testid="delete-modal"
    >
      <div className={styles.modalBody}>
        <p>{t('developerPlatform.apiKeys.delete.text')}</p>
        <div className={styles.field}>
          <label htmlFor="dp-delete-confirm">
            {t('developerPlatform.apiKeys.delete.confirmLabel')}
          </label>
          <Input
            id="dp-delete-confirm"
            value={confirmation}
            onChange={event => setConfirmation(event.target.value)}
            placeholder={apiKey.name}
            maxLength={80}
            data-testid="delete-confirm-input"
          />
        </div>
      </div>
    </Modal>
  )
}
